import { authedFetch } from "./authedFetch";
import { buildMentionEmail } from "./mentionEmail";

const ENDPOINT =
  "https://us-central1-team-edelstein.cloudfunctions.net/api/send-email-custom";

/**
 * POST an OH email to the Cloud Function.
 *
 * @param {Object} args
 * @param {string} args.userId          - sender's user doc ID
 * @param {string} args.to              - recipient address
 * @param {string} args.toName          - recipient display name (logged only)
 * @param {string} args.subject
 * @param {string} args.text            - plain-text body (required; fallback)
 * @param {string} [args.html]          - HTML body (optional; preferred)
 * @param {string} args.type            - "scheduling" | "update" | "cancellation" | "recap" | "invite-resend"
 * @param {string} [args.relatedDealId]
 * @param {string} [args.relatedOpenHouseId]
 * @returns {Promise<{ok: boolean, error?: string, gmailMessageId?: string}>}
 */
export async function sendOpenHouseEmail({
  userId, to, toName, subject, text, html, type,
  relatedDealId, relatedOpenHouseId,
}) {
  try {
    const res = await authedFetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        userId, to, toName, subject,
        body: text,                     // wire name stays `body` for backend compat
        html: html || undefined,        // omit field entirely when absent
        type, relatedDealId, relatedOpenHouseId,
      }),
    });
    if (res.ok) {
      const data = await res.json().catch(() => ({}));
      return { ok: true, ...data };
    }
    let errorText = `HTTP ${res.status}`;
    try {
      const data = await res.json();
      errorText = data.error || errorText;
    } catch { /* ignore */ }
    return { ok: false, error: errorText };
  } catch (err) {
    return { ok: false, error: err?.message || "Network error" };
  }
}

/**
 * Send one transaction email. Same endpoint as OH emails, plus:
 * - html (branded body; `text` remains the plain fallback, wire name `body`)
 * - attachments: [{name, source: "drive"|"storage", fileId?, storagePath?, size}]
 *   (requires the updated Cloud Function — see docs/backend/send-email-custom-attachments.md)
 */
export async function sendTransactionEmail({
  userId, to, toName, cc, bcc, subject, text, html, attachments, emailId, relatedDealId,
}) {
  try {
    const payload = {
      userId, to, toName, cc: cc || undefined, bcc: bcc || undefined, subject,
      body: text,
      html: html || undefined,
      type: `transaction:${emailId}`,
      relatedDealId,
    };
    if (Array.isArray(attachments) && attachments.length > 0) {
      payload.attachments = attachments;
    }
    const res = await authedFetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (res.ok) {
      const data = await res.json().catch(() => ({}));
      return { ok: true, ...data };
    }
    let errorText = `HTTP ${res.status}`;
    try {
      const data = await res.json();
      errorText = data.error || errorText;
    } catch { /* ignore */ }
    return { ok: false, error: errorText };
  } catch (err) {
    return { ok: false, error: err?.message || "Network error" };
  }
}

/**
 * Send one Vendor Directory recommendation to a client. Same endpoint and
 * shape as the other senders; typed so the email log can tell them apart.
 */
export async function sendVendorEmail({ userId, to, toName, subject, text, html }) {
  try {
    const res = await authedFetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        userId, to, toName, subject,
        body: text,
        html: html || undefined,
        type: "vendor-recommendation",
      }),
    });
    if (res.ok) {
      const data = await res.json().catch(() => ({}));
      return { ok: true, ...data };
    }
    let errorText = `HTTP ${res.status}`;
    try {
      const data = await res.json();
      errorText = data.error || errorText;
    } catch { /* ignore */ }
    return { ok: false, error: errorText };
  } catch (err) {
    return { ok: false, error: err?.message || "Network error" };
  }
}

/**
 * Send one Past Client Follow-Up check-in email. Same endpoint and shape as
 * the other senders; typed `followup:<touchKey>` so the email log can tell
 * them apart. `bcc` matches the app-wide convention (see splitToBcc in
 * emailFanout.js): a comma-joined string of the remaining household clients,
 * passed straight through to the Gmail `Bcc:` header by the backend.
 */
export async function sendFollowUpEmail({
  userId, to, toName, subject, text, html, touchKey, relatedDealId, bcc,
}) {
  try {
    const res = await authedFetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        userId, to, toName, subject,
        body: text,
        html: html || undefined,
        bcc: bcc || undefined,
        type: `followup:${touchKey}`,
        relatedDealId,
      }),
    });
    if (res.ok) {
      const data = await res.json().catch(() => ({}));
      return { ok: true, ...data };
    }
    let errorText = `HTTP ${res.status}`;
    try {
      const data = await res.json();
      errorText = data.error || errorText;
    } catch { /* ignore */ }
    return { ok: false, error: errorText };
  } catch (err) {
    return { ok: false, error: err?.message || "Network error" };
  }
}

/**
 * Notify a teammate by email that they were @-mentioned.
 *
 * Internal team mail, so it goes through the same custom-send endpoint as the
 * rest (falling back to the team Gmail account when the sender has not
 * connected their own). Wording lives in mentionEmail.js.
 *
 * @param {Object} args
 * @param {string} args.userId        - sender's user doc ID
 * @param {string} args.to            - mentioned teammate's address
 * @param {string} args.toName        - mentioned teammate's display name
 * @param {string} args.senderName    - who did the tagging
 * @param {string} args.where         - e.g. "a call sheet note on 12 Main St"
 * @param {string} args.text          - the note/message text, quoted in the body
 * @param {string} [args.relatedDealId]
 * @returns {Promise<{ok: boolean, error?: string}>}
 */
export async function sendMentionEmail({
  userId, to, toName, senderName, where, text, relatedDealId,
}) {
  const { subject, text: body, html } = buildMentionEmail({
    senderName, toName, where, text,
  });
  try {
    const res = await authedFetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        userId, to, toName, subject,
        body,
        html,
        type: "mention",
        relatedDealId,
      }),
    });
    if (res.ok) {
      const data = await res.json().catch(() => ({}));
      return { ok: true, ...data };
    }
    let errorText = `HTTP ${res.status}`;
    try {
      const data = await res.json();
      errorText = data.error || errorText;
    } catch { /* ignore */ }
    return { ok: false, error: errorText };
  } catch (err) {
    return { ok: false, error: err?.message || "Network error" };
  }
}

/**
 * Fallback: open the user's default mail client with a pre-filled draft.
 * Used when the backend endpoint isn't reachable; the OH itself is still saved.
 */
export function openMailtoFallback({ to, subject, body }) {
  const params = new URLSearchParams();
  if (subject) params.set("subject", subject);
  if (body) params.set("body", body);
  const url = `mailto:${encodeURIComponent(to)}?${params.toString()}`;
  if (typeof window !== "undefined") {
    window.location.href = url;
  }
}
